import React from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useLocation} from 'react-router-dom'

import {Box, Theme} from '@mui/material'
import {PGButton, PGTypography} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import Session from '@progyny/pages/Consultations/Program/components/Session'
import {ConsultationCard} from '@progyny/pages/Consultations/components/ConsultationCard'

export interface SessionItem {
  sessionIndex: string
  title: string
  subtitle: string
  description: string
  status:
    | 'completed'
    | 'upcoming'
    | 'readyForBooking'
    | 'locked'
    | 'notAttended'
    | 'skipped'
  appointment: any
  required: boolean
}

export interface ProgramOverviewCardProps {
  programSlug: string
  programDescription: string | undefined | null
  bookingStrategy: string | undefined | null
  isEnrolled: boolean
  canEnrollInPrograms: boolean
  enableLearnMore: boolean
  showBookingButton: boolean
  sessions: SessionItem[]
  colors?: any
  enrolling: boolean
  onEnroll: () => void
  onCancelRequested?: (cancelUrl: string) => void
}

const useStyles = makeStyles<{colors?: any}>()((theme: Theme, {colors}) => ({
  overviewCard: {
    width: '100%',
    boxSizing: 'border-box',
    background: colors?.secondary || '#E1F4F7',
    padding: '32px',
    borderRadius: '16px',
  },
  overviewCardHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '32px',
  },
  overviewContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  overviewTitle: {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '150%',
    letterSpacing: '0.5px',
    color: '#000000',
    marginBottom: '8px',
  },
  overviewDescription: {
    maxWidth: '780px',
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '18px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: '#000000',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'center',
    flexShrink: 0,
  },
  sessionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '32px',
  },
  primaryButton: {
    '&.Mui-disabled': {
      opacity: 0.3,
      backgroundColor: `${colors?.primary} !important`,
      color: '#FFFFFF !important',
    },
  },
}))

const ProgramOverviewCard: React.FC<ProgramOverviewCardProps> = ({
  programSlug,
  programDescription,
  bookingStrategy,
  isEnrolled,
  canEnrollInPrograms,
  enableLearnMore,
  showBookingButton,
  sessions,
  colors,
  enrolling,
  onEnroll,
  onCancelRequested,
}) => {
  const {classes} = useStyles({colors})
  const {t} = useTranslation()
  const history = useHistory()
  const location = useLocation()

  return (
    <Box className={classes.overviewCard}>
      <Box className={classes.overviewCardHeader}>
        <Box className={classes.overviewContent}>
          <PGTypography className={classes.overviewTitle}>
            {programSlug === 'pregnancy-support'
              ? t(
                  'consultations_program_session_plan_pregnancy_program_overview'
                )
              : programSlug === 'postpartum-support'
                ? t(
                    'consultations_program_session_plan_postpartum_program_overview'
                  )
                : t('consultations_program_session_plan_program_overview')}
          </PGTypography>
          <PGTypography className={classes.overviewDescription}>
            {programDescription}
          </PGTypography>
        </Box>

        <Box className={classes.buttonContainer}>
          {enableLearnMore && (
            <PGButton
              variant='text'
              label={t('common_learn_more')}
              onClick={() => {
                history.push(`/consultations/program/${programSlug}/info/1`, {
                  referrer: location.pathname,
                })
              }}
              size='medium'
            />
          )}
          {!isEnrolled && (
            <PGButton
              variant='contained'
              label={t('consultations_program_session_plan_enroll_now')}
              onClick={onEnroll}
              loading={enrolling}
              size='medium'
              disabled={!canEnrollInPrograms}
              className={classes.primaryButton}
            />
          )}
          {bookingStrategy === 'PRE_BOOK_ALL' &&
            isEnrolled &&
            sessions.length > 0 && (
              <PGButton
                variant='contained'
                label={t('consultations_program_session_plan_start_booking')}
                onClick={() =>
                  history.push(`/consultations/program/${programSlug}/book`)
                }
                size='medium'
              />
            )}
        </Box>
      </Box>

      {sessions.length > 0 && (
        <ConsultationCard
          padding='md'
          borderRadius='md'
          className={classes.sessionsContainer}
        >
          {sessions.map((session, index) => (
            <Session
              key={index}
              programSlug={programSlug}
              sessionIndex={session.sessionIndex}
              title={session.title}
              subtitle={session.subtitle!}
              description={session.description}
              status={session.status}
              isOptional={!session.required}
              showBookingButton={showBookingButton}
              enableLearnMore={enableLearnMore}
              colors={colors}
              isLastItem={index === sessions.length - 1}
              nextIsLocked={
                index < sessions.length - 1 &&
                sessions[index + 1].status === 'locked'
              }
              appointment={session.appointment}
              onCancelRequested={onCancelRequested}
            />
          ))}
        </ConsultationCard>
      )}
    </Box>
  )
}

export default ProgramOverviewCard
